"use client";

import { useGetProjects } from "@/hooks/main-hook";
import { useLocale } from "next-intl";
import { useState } from "react";
import Image from "next/image";
import { Search, CreditCard, ArrowUpRight } from "lucide-react";
import ProjectLoading from "./ProjectLoading";
import Paginate from "../paginate/paginate";
import Link from "next/link";

export default function MainProjects() {
  const locale = useLocale();
  const isAr = locale === "ar";

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const { data, isLoading } = useGetProjects({
    page: currentPage,
    search: searchTerm,
  });

  const projects = data?.projects || [];
  const paginate = data?.paginate;

  return (
    <div className="min-h-screen p-4 md:p-8" dir={isAr ? "rtl" : "ltr"}>
      <div className="mx-auto">
        {/* Header & Search Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              {isAr ? "المشاريع المتاحة" : "Available Projects"}
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              {isAr
                ? `إجمالي المشاريع: ${paginate?.totalItems || 0}`
                : `Total Projects: ${paginate?.totalItems || 0}`}
            </p>
          </div>

          <div className="relative w-full md:w-96">
            <input
              type="text"
              placeholder={isAr ? "ابحث عن مشروع..." : "Search projects..."}
              className="w-full pl-10 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search
              className={`absolute top-3.5 w-5 h-5 text-gray-400 ${isAr ? "left-3" : "right-3"}`}
            />
          </div>
        </div>

        {/* Projects Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, index) => (
              <ProjectLoading key={index} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project: any) => (
              <Link
                href={`/real-estate-projects/${project?.slug}`}
                key={project.id}
                className="group bg-white rounded-4xl p-3 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] transition-all duration-500 flex flex-col"
              >
                {/* Project Thumbnail */}
                <div className="relative h-56 md:h-64 w-full rounded-3xl overflow-hidden">
                  <Image
                    src={project.thumbnail}
                    alt={project.title[locale]}
                    fill
                    className="object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  {/* Price Tag Overlay */}
                  <div
                    className={`absolute bottom-4 ${isAr ? "left-4" : "right-4"} bg-white/90 backdrop-blur-md px-4 py-2 rounded-2xl shadow-sm`}
                  >
                    <span className="text-blue-700 font-bold text-sm md:text-base">
                      {project.price.toLocaleString()} {isAr ? "ر.س" : "SAR"}
                    </span>
                  </div>
                </div>

                {/* Project Content */}
                <div className="p-4 flex flex-col grow">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-extrabold text-gray-800 line-clamp-1 group-hover:text-blue-600 transition-colors">
                      {project.title[locale]}
                    </h3>
                  </div>

                  <p className="text-gray-500 text-sm leading-relaxed line-clamp-2 mb-4 h-10">
                    {project.description[locale]}
                  </p>

                  {/* Units Info */}
                  <div className="flex items-center gap-2 text-xs font-semibold text-gray-400 bg-gray-50 w-fit px-3 py-1.5 rounded-full mb-6">
                    <CreditCard className="w-3.5 h-3.5 text-blue-500" />
                    <span>
                      {project.totalUnits}{" "}
                      {isAr ? "وحدة متاحة" : "Available Units"}
                    </span>
                  </div>

                  {/* Footer: User Info */}
                  <div className="mt-auto pt-4 border-t border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="relative w-10 h-10 rounded-full overflow-hidden ring-2 ring-gray-50">
                        <Image
                          src={project.user.photo || "/user.webp"}
                          alt={project.user.username}
                          fill
                          className="object-cover"
                        />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm  capitalize font-bold text-gray-700 leading-none mb-1">
                          {project.user.username}
                        </span>
                        {project.user.isVerified && (
                          <div className="flex items-center gap-1">
                            <div className="relative w-5 h-5">
                              <Image
                                src="/verified.png"
                                fill
                                alt="v"
                                className="object-contain pointer-events-none select-none"
                              />
                            </div>
                            <span className="text-[10px] text-blue-500 font-bold uppercase tracking-tight">
                              {isAr ? "موثق" : "Verified"}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Icon */}
                    <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-all">
                      <ArrowUpRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {!isLoading && projects.length === 0 && (
          <div className="text-center py-20 bg-white rounded-2xl border-2 border-dashed border-gray-200">
            <p className="text-gray-400 italic">
              {isAr
                ? "لا توجد مشاريع مطابقة للبحث"
                : "No projects found matching your search"}
            </p>
          </div>
        )}

        {paginate?.totalPages >= 1 && (
          <Paginate
            currentPage={currentPage}
            totalPages={paginate?.totalPages || 1}
            setCurrentPage={setCurrentPage}
          />
        )}
      </div>
    </div>
  );
}
